import React, { useRef, useState, useEffect, createRef, useCallback } from 'react'
import { XDivider, YDivider } from '../../../irmlib/src/styles'
import { MainRouteSubRoutePage } from '../../AppControl/RoutePage'
import { useFormPageHandler, useMergeState } from 'irmlib/hooks'
import { OutlinedButton, Label, ContainedButton } from 'irmlib/components/Controls'
import { CancelButton, TextField, SaveButton, Select } from 'irmlib/components/FormControls'
import { openGeneralDialog, closeGeneralDialog, setBusy, clearBusy } from 'irmlib/containers'
import * as routePaths from '../../../navigation/routePaths'
import QcDataGridBulkUpdateTable from '../../Controls/QcDataGridBulkUpdateTable'
import SummaryHeader from '../../Controls/SummaryHeader'
import { useLocation, useHistory } from 'react-router-dom'
import * as gdeTransactions from '../../../services/firestore/gdeTransactions'
import * as gdeTransactionsLite from '../../../services/firestore/gdeTransactionsLite'
import * as constants from 'irmlib/common/constants'
import PropTypes from 'prop-types'
import { getUser } from '../../../redux/global'
import { getFunctions, httpsCallable } from '../../../utils/firestore'
import { errorToast, successToast } from '../../../common/functions'
import {
  filterRowsBySelection,
  validateQcGridField,
  collectValidationErrorsForApprove,
  buildValidationErrorsBatched,
} from './qcValidationHelpers'
import { QC_BOX_ROWS_BUILD_BATCH } from '../../../common/constants'
import { useTranslation } from 'react-i18next'

const functions = getFunctions()


const defaultSelectOption = { value: constants.defaultSelect, label: constants.defaultSelect }
const SKP_BOX_ID = 'skpBoxId'
const CUSTOMER_ID = 'customerId'

const DATA_ENTRY_USER = 0
const DESCRIPTION = 1
const VERIFICATION_USER = 2

const Buttons = props =>
  <>
    <CancelButton onClick={props.onClickCancel} noTranslate={true} label={props.t('buttons.cancel')}/>
    <XDivider width='10px' />
    <OutlinedButton disabled={props.changeApprove || props.disabled} onClick={props.onClickReject} noTranslate={true}>{props.t('buttons.reject')}</OutlinedButton>
    <XDivider width='10px' />
    <ContainedButton disabled={props.changeApprove || props.disabled} onClick={props.onClickApprove} noTranslate={true}>{props.t('buttons.approve')}</ContainedButton>
  </>

Buttons.propTypes = {
  onClickApprove: PropTypes.func,
  onClickReject: PropTypes.func,
  onClickCancel: PropTypes.func,
  changeApprove: PropTypes.bool,
  disabled: PropTypes.bool,
  t: PropTypes.any
}

const ButtonsForApprove = props =>
  <>
    <CancelButton onClick={closeGeneralDialog} noTranslate={true} label={props.t('buttons.cancel')}/>
    <XDivider width='10px' />
    <ContainedButton onClick={props.postService} noTranslate={true}>{props.t('buttons.approve')}</ContainedButton>
  </>

ButtonsForApprove.propTypes = {
  postService: PropTypes.func,
  t: PropTypes.any
}

const ButtonsForCancel = props =>
  <>
    <OutlinedButton onClick={props.goBack} noTranslate={true}>{props.t('buttons.no')}</OutlinedButton>
    <XDivider width='10px' />
    <ContainedButton onClick={props.postService} noTranslate={true}>{props.t('buttons.yes')}</ContainedButton>
  </>

ButtonsForCancel.propTypes = {
  postService: PropTypes.func,
  goBack: PropTypes.func,
  t: PropTypes.any
}

const VerticalDivider = () =>
  <YDivider height='15px' />

const sortBoxDocs = (docs) => [...docs].sort((a, b) => {
  const boxSrNoA = a.data().boxSrNo || a.data().boxId
  const boxSrNoB = b.data().boxSrNo || b.data().boxId
  return boxSrNoA > boxSrNoB ? 1 : -1
})

const buildSingleBoxRow = (doc, editableFieldNames) => {
  const boxInfo = doc.data().boxInfo ?? {}
  const row = {
    SKP_Box_ID: doc.id,
    boxId: doc.id,
    tableData: { id: doc.id },
    ...boxInfo,
    skpBoxId: boxInfo.skpBoxId || doc.id,
  }
  editableFieldNames.forEach((name) => {
    if (row[name] === undefined || row[name] === null) {
      row[name] = ''
    }
  })
  return row
}

const buildBoxRowsBatched = (sortedDocs, editableFieldNames, onComplete) => {
  const boxList = []
  let rowIndex = 0
  let frameId = null
  let cancelled = false

  const step = () => {
    if (cancelled) {
      return
    }
    const end = Math.min(rowIndex + QC_BOX_ROWS_BUILD_BATCH, sortedDocs.length)
    for (let i = rowIndex; i < end; i++) {
      boxList.push(buildSingleBoxRow(sortedDocs[i], editableFieldNames))
    }
    rowIndex = end
    if (rowIndex < sortedDocs.length) {
      frameId = requestAnimationFrame(step)
    } else {
      onComplete(boxList)
    }
  }

  frameId = requestAnimationFrame(step)
  return () => {
    cancelled = true
    if (frameId != null) {
      cancelAnimationFrame(frameId)
    }
  }
}

const buildBoxColumnsFromFields = (fieldKeyList, boxes, jobId, jobDetails, regexFormat, t) => {
  const columnList = [{
    title: t('tableColumns.skpBoxId'),
    field: 'SKP_Box_ID',
    headerAlign: 'center',
    editable: 'never',
  }]
  let isVerifyRequired = false

  fieldKeyList.forEach((element) => {
    if (element.isSelected && element.name !== SKP_BOX_ID && element.name !== CUSTOMER_ID) {
      columnList.push({
        title: element.customName ? element.customName : element.name,
        field: element.name,
        headerAlign: 'center',
        editable: 'always',
        doNotDuplicate: element.doNotDuplicate,
        validate: (rowData) => validateQcGridField({
          element,
          rowData,
          fieldKeyList,
          jobId,
          contextRows: boxes,
          jobDetails,
          regexFormat,
          translateFn: t,
        }),
      })
      if (element.verify) {
        isVerifyRequired = true
      }
    }
  })

  return { columnList, isVerifyRequired }
}

const getBoxRowId = (row) => String(row?.boxId ?? row?.skpBoxId ?? row?.SKP_Box_ID ?? '')

const stripGridMetadata = (row) => {
  const rowCopy = { ...row }
  delete rowCopy.rowSelected
  delete rowCopy.tableData
  delete rowCopy.SKP_Box_ID
  delete rowCopy.__bulkUpdateRowId
  return rowCopy
}

const initialState = {
  showTable: false,
  boxDoc: [],
  job: [],
  boxes: [],
  templateName: '',
  fields: [],
  dataEntryUser: '',
  verificationUser: '',
  columns: [],
  fieldValues: [],
  users: [defaultSelectOption],
  verifyUser: [],
  entryUser: [],
  isVerifyRequired: false,
  assignedUsers: {},
  workerList: [],
  selectedBoxes: [],
  autoTransmit: false,
  jobStatus: '',
  jobId: '',
  country: '',
  skpinstance: '',
  regexFormat: '',
  jobDetails: null,
}

let loggedInUserDistrict
let gdeConfigPromise

const loadGdeConfigCached = () => {
  if (!gdeConfigPromise) {
    gdeConfigPromise = gdeTransactions.getGdeConfig()
  }
  return gdeConfigPromise
}

const getService = async (setState) => {
  const getObject = {
  }
  return Promise.resolve(getObject)
}

const BoxFlowForQualityCheck = (props) => {
  const { t } = useTranslation()
  const refLocalBusyCursor = useRef(null)
  const [state, setState, getState] = useMergeState(initialState)
  const [changeApprove, setChangeApprove] = useState(true)

 
  const formTemplate = [
    {
      label: t('tableColumns.dataEntryUser'),
      fieldKey: 'newDataEntryUser',
      isRequired: true,
      defaultValue: getState().dataEntryUser
    },
    {
      label: t('dataEntry.Description'),
      fieldKey: 'description',
      isRequired: true,
      maxLength: 30
    }
  ]
  const [arrRefs, data] = useFormPageHandler({
    formTemplate,
    service: getService,
    busyOverride: refLocalBusyCursor,
  })
  
  const history = useHistory()
  const location = useLocation()
  const BoxId = location.pathname.split('/')[3]
  const JobId = location.pathname.split('/')[2]
  const tableRef = useRef(null)
  const boxRowsBuildCancelRef = useRef(null)
  const validationCancelRef = useRef(null)
  // AI code start — upfront cell validation state
  const [validationErrors, setValidationErrors] = useState({})
  const [tableLoading, setTableLoading] = useState(true)
  const [isGridRowEditing, setIsGridRowEditing] = useState(false)
  const workersLoadedRef = useRef(false)

  const refreshValidationErrors = useCallback((rows) => {
    const { boxes, fields, jobDetails, regexFormat } = getState()
    const gridRows = rows || tableRef.current?.getRows?.() || boxes
    if (!gridRows?.length || !fields?.length) {
      setValidationErrors({})
      return
    }

    validationCancelRef.current?.()
    validationCancelRef.current = buildValidationErrorsBatched({
      rowList: gridRows,
      fields,
      jobId: JobId,
      jobDetails,
      regexFormat,
      onProgress: setValidationErrors,
      onComplete: setValidationErrors,
      getRowId: getBoxRowId,
    })
  }, [getState, JobId])

  const handleGridValidationChange = useCallback((_errors, isRowEditing) => {
    setIsGridRowEditing(isRowEditing)
    if (!isRowEditing) {
      refreshValidationErrors()
    }
  }, [refreshValidationErrors])

  const handleRowsUpdated = useCallback((rows) => {
    setState({ boxes: rows })
    refreshValidationErrors(rows)
  }, [setState, refreshValidationErrors])

  useEffect(() => () => {
    validationCancelRef.current?.()
  }, [])
  // AI code end

  const getSelectedBoxPayload = useCallback(() => {
    const gridRows = tableRef.current?.getRows?.() || getState().boxes
    const selectedIds = new Set(
      getState().selectedBoxes.map((b) => getBoxRowId(b)),
    )
    const boxArray = {}
    gridRows.forEach((row) => {
      const boxId = getBoxRowId(row)
      if (!boxId || !selectedIds.has(boxId)) {
        return
      }
      const rowCopy = stripGridMetadata(row)
      const key = boxId
      delete rowCopy.boxId
      rowCopy.skpBoxId = boxId
      boxArray[key] = rowCopy
    })
    return boxArray
  }, [getState])

  useEffect(() => {
    let cancelled = false
    const loadPage = async () => {
      const user = getUser()
      loggedInUserDistrict = user.district
      if (!cancelled) {
        await getBoxData()
      }
    }
    loadPage()
    return () => {
      cancelled = true
      boxRowsBuildCancelRef.current?.()
    }
  }, [])

  const ButtonsForReject = props =>
    <>
      <CancelButton onClick={closeGeneralDialog} noTranslate={true} label={props.t('buttons.cancel')}/>
      <XDivider width='10px' />
      <SaveButton formTemplate={formTemplate} arrRefs={arrRefs} postService={props.onClickReject} noTranslate={true} saveLabel={t('buttons.reject')} />
    </>

  ButtonsForReject.propTypes = {
    onClickReject: PropTypes.func,
    t: PropTypes.any
  }

  const ContentForReject = props => {
    if (getState().isVerifyRequired) {
      arrRefs.push(createRef())
      formTemplate.push(
        {
          label: t('tableColumns.verificationUser'),
          fieldKey: 'newVerificationUser',
          isRequired: true,
          defaultValue: getState().verificationUser
        }
      )
    }

    return (
      <>
        <Label noTranslate={true}>{t('qualityCheck.rejectMsg')}</Label>
        <VerticalDivider />
        <Select noTranslateItems
          data={data}
          fieldTemplate={formTemplate[DATA_ENTRY_USER]}
          items={state.entryUser}
          ref={arrRefs[DATA_ENTRY_USER]}
          noTranslate= {true}
        />
        <VerticalDivider />
        { state.isVerifyRequired
          ? <>
            <Select noTranslateItems
              data={data}
              fieldTemplate={formTemplate[VERIFICATION_USER]}
              items={state.verifyUser}
              ref={arrRefs[VERIFICATION_USER]}
              noTranslate= {true}
            />
            <VerticalDivider />
          </>
          : null
        }
        <TextField
          data={data}
          fieldTemplate={formTemplate[DESCRIPTION]}
          ref={arrRefs[DESCRIPTION]}
          noTranslate= {true}
        />
      </>
    )
  }

  const onSelectionChange = useCallback((rows) => {
    if (rows.length > 0) {
      setChangeApprove(false)
    } else {
      setChangeApprove(true)
    }
    setState({ selectedBoxes: rows })
  }, [setState])

  const manualTransmitJob = async() => {
    const JobState = getState().jobId
    const data = {
      jobStatus: constants.jobStatus.readyToTransmit,
      autoTransmitAndStatus: `${getState().autoTransmit}-${constants.jobStatus.readyToTransmit}`,
    }
    await gdeTransactions.updateJob(JobState, data)
    if (getState().country !== 'gb') {
      const callabletransmit = httpsCallable(functions, 'grpGde-ManuallyExportSkpApprovedAssets')
      callabletransmit({ jobId: JobState }).then((result) => {
        successToast({ message: t('toast.transmitQueued', { jobId: JobState }) })
      }).catch(error => {
        errorToast({ message: error = t('toast.manualTransmitFailMessage'), title: t('toast.manualTransmitFailTitle') })
        clearBusy()
      })
    }
    closeGeneralDialog()
    history.push(`${routePaths.myJobs}`)
  }

  const openApproveModal = async () => {
    if (tableLoading) {
      return
    }

    if (isGridRowEditing) {
      errorToast({ message: t('toast.error') })
      return
    }

    const { boxes, fields, jobDetails, regexFormat, selectedBoxes } = getState()
    if (!selectedBoxes?.length) {
      errorToast({ message: t('toast.error') })
      return
    }

    const gridRows = tableRef.current?.getRows?.() || boxes
    const rowsToValidate = filterRowsBySelection(gridRows, selectedBoxes, getBoxRowId)
    const allErrors = collectValidationErrorsForApprove({
      rowsToValidate,
      allRows: gridRows,
      fields,
      jobId: JobId,
      jobDetails,
      regexFormat,
      getRowId: getBoxRowId,
    })
    setValidationErrors(allErrors)

    if (Object.keys(allErrors).length > 0) {
      errorToast({ message: t('forms.formValidationError') })
      return
    }

    const jobDetailsDoc = await gdeTransactions.getJobById(JobId)
    if (jobDetailsDoc.data().autoTransmit === undefined || jobDetailsDoc.data().autoTransmit === 'true') {
      openGeneralDialog({
        title: t('qualityCheck.approveTransmit'),
        message: t('qualityCheck.approveBoxesMsgForautoTransmit'),
        buttons: <ButtonsForApprove postService={postService} t={t}/>,
        translateMessage: false,
        noTranslateTitle: true,
        closeX: true
      })
    } else {
      openGeneralDialog({
        title: t('qualityCheck.approveTransmit'),
        message: t('qualityCheck.approveBoxesMsgForManualTransmit'),
        buttons: <ButtonsForApprove postService={postService} t={t}/>,
        translateMessage: false,
        noTranslateTitle: true,
        closeX: true
      })
    }
  }
  
  const statusUpdateForJob = async (jobDetail) => {
    const collection = await gdeTransactionsLite.getBoxesForJob(JobId)
    const boxList = []
    const boxUserList = []

    collection.forEach((doc) => {
      const data = doc.data()
      boxList.push({
        boxId: data.boxId,
        entryUser: data.dataEntryUser,
        status: data.status,
        dataEntryUser: data.dataEntryUser,
        verificationUser: data.verificationUser
      })
      boxUserList.push(doc.data().dataEntryUser)
    })
    const checkReadyToTransmit = (Boxes) => {
      if (Boxes.status === constants.boxStatus.approved) {
        return Boxes.status === constants.boxStatus.approved 
      }
      if (Boxes.status === constants.boxStatus.transmitted) {
        return Boxes.status === constants.boxStatus.transmitted 
      }
      if (Boxes.status === constants.boxStatus.exception) {
        return Boxes.status === constants.boxStatus.exception
      }
    }

    const result = boxList.filter(checkReadyToTransmit)


    const transmitBoxEqual = boxList.length === 0 ? false : boxList.length === result.length
    
  
    if (transmitBoxEqual && jobDetail.totalBoxes !== 9999) {
      const data = {
        jobStatus: constants.jobStatus.readyToTransmit,
        autoTransmitAndStatus: `${jobDetail.autoTransmit}-${constants.jobStatus.readyToTransmit}`,
      }
      await gdeTransactions.updateJob(JobId, data)
      manualTransmitJob()
    }
  }


  const openRejectModal = async () => {
    if (!workersLoadedRef.current) {
      setBusy()
      try {
        await getJobData()
        workersLoadedRef.current = true
      } finally {
        clearBusy()
      }
    }
    openGeneralDialog({
      title: t('qualityCheck.rejectModalTitle'),
      message: <ContentForReject/>,
      buttons: <ButtonsForReject onClickReject={onClickReject} t={t}/>,
      translateMessage: false,
      noTranslateTitle: true,
      buttonsPlacement: 'center',
      closeX: true
    })
  }

  const openCancelModal = () => {
    openGeneralDialog({
      title: t('qualityCheck.cancelQC'),
      message: t('qualityCheck.cancelMsg'),
      buttons: <ButtonsForCancel postService={saveOnCancel} goBack={goBack} t={t}/>,
      translateMessage: false,
      noTranslateTitle: true,
      closeX: true
    })
  }

  const goBack = () => {
    history.goBack()
    closeGeneralDialog()
  }


  const onClickReject = async (postObject) => {
    closeGeneralDialog()
    const { assignedUsers, workerList, isVerifyRequired } = getState()
    const reKeyData = []
    const { newDataEntryUser: entryUser, newVerificationUser: verifyUser = '' } = postObject

    if (entryUser in assignedUsers) {
      assignedUsers[entryUser].verifyUser = isVerifyRequired ? verifyUser : ''
      if (!assignedUsers[entryUser].jobCategory.includes('Data Entry User')) {
        assignedUsers[entryUser].jobCategory.push('Data Entry User')
      }
    } else {
      assignedUsers[entryUser] = {
        completedBoxes: 0,
        jobCategory: ['Data Entry User'],
        verifyUser: isVerifyRequired ? verifyUser : '',
        workerName: `${workerList[entryUser].firstName} ${workerList[entryUser].lastName}`
      }
    }

    if (isVerifyRequired) {
      if (verifyUser in assignedUsers) {
        if (!assignedUsers[entryUser].jobCategory.includes('Verification User')) {
          assignedUsers[entryUser].jobCategory.push('Verification User')
        }
      } else {
        assignedUsers[verifyUser] = {
          completedBoxes: 0,
          jobCategory: ['Verification User'],
          verifyUser: '',
          workerName: `${workerList[verifyUser].firstName} ${workerList[verifyUser].lastName}`
        }
      } 
    }

    getState().selectedBoxes.forEach(async(box) => {
      const boxId = box.boxId
      if (getState().isVerifyRequired) {
        reKeyData[boxId] = {
          dataEntryUser: entryUser,
          verificationUser: verifyUser,
          status: constants.boxStatus.requiresReKey
        }      
      } else {
        reKeyData[boxId] = {
          dataEntryUser: entryUser,
          status: constants.boxStatus.requiresReKey
        }
      }

      const jobBoxMetricsData = await gdeTransactions.getMetricsForJobBox(JobId, boxId)
      jobBoxMetricsData.forEach((doc) => {
        const data = doc.data()
        const { metricsFileId, totalBoxCount } = data
        if (metricsFileId !== undefined) {
          const metricsData = {
            totalRecordCount: 0,
            totalBoxCount: 0,
            updatedTs: new Date(),
          }
          totalBoxCount > 0 && gdeTransactions.updateMetricsForJobBox(doc.id, metricsData)
        }
      })
    })

    const assignUserList = Object.keys(assignedUsers)

    const jobData = {
      assignedUsers: assignedUsers,
      assignedUserList: assignUserList
    }
    
    await gdeTransactions.updateJob(JobId, jobData)
    await gdeTransactionsLite.updateBoxFlow(JobId, constants.boxStatus.requiresReKey, reKeyData, false)
    history.goBack()
  }

  const postService = async (postObject) => {
    const jobDetails = await gdeTransactions.getJobById(JobId)

    closeGeneralDialog()
    const boxArray = getSelectedBoxPayload()
    await gdeTransactionsLite.updateBoxFlow(JobId, constants.boxStatus.approved, boxArray).then(async () => {
      await statusUpdateForJob(jobDetails.data())
    })
    history.push(`${routePaths.qualityCheck}`)
    return Promise.resolve()
  }

  const saveOnCancel = async () => {
    const jobDetail = await gdeTransactions.getJobById(JobId)
    const jobDetails = jobDetail.data()
    closeGeneralDialog()
    const boxArray = getSelectedBoxPayload()
    await gdeTransactionsLite.updateBoxFlow(JobId, constants.boxStatus.approved, boxArray).then(async () => {
      await statusUpdateForJob(jobDetails)
      history.goBack()
    })
    return Promise.resolve()
  }
  
  const getJobData = async () => {
    // const workerCollection = await gdeTransactions.getWorkersWithDistrict(loggedInUserDistrict)
    const workerCollection = await gdeTransactions.getworkersLists(loggedInUserDistrict)
    const workerDropList = [defaultSelectOption]
    const workerList = []
    workerCollection.forEach((doc) => {
      workerDropList.push({
        value: doc.id,
        label: `${doc.id}-${doc.data().firstName} ${doc.data().lastName}`
      })
      workerList[doc.id] = doc.data()
    })
   
    setState({ entryUser: workerDropList, verifyUser: workerDropList, workerList: workerList })
  }

  const getBoxData = async () => {
    setTableLoading(true)
    boxRowsBuildCancelRef.current?.()
    const [collection, documentJob, gdeConfigParts] = await Promise.all([
      gdeTransactionsLite.getBoxesForQC(JobId),
      gdeTransactions.getJobById(JobId),
      loadGdeConfigCached(),
    ])
    const jobDoc = documentJob.data()
    const [, , , , skpEncoding] = gdeConfigParts
    const regexFormat = skpEncoding[jobDoc?.countryCode]?.regex
    const boxKeyProps = jobDoc.templateData
    const verificationUser = collection.docs[0]?.data().verificationUser
    const dataEntryUser = collection.docs[0]?.data().dataEntryUser
    let fieldKeyList = boxKeyProps.templateFields
    const assignedUsers = jobDoc.assignedUsers

    fieldKeyList = Object.keys(fieldKeyList).map((key) => {
      const data = fieldKeyList[key]
      data.name = key
      return data
    })

    fieldKeyList.sort((a, b) => (a.customOrder > b.customOrder) ? 1 : ((b.customOrder > a.customOrder) ? -1 : 0))

    const sortedDocs = sortBoxDocs(collection.docs)
    const editableFieldNames = fieldKeyList
      .filter((el) => el.isSelected && el.name !== SKP_BOX_ID && el.name !== CUSTOMER_ID)
      .map((el) => el.name)

    const finishLoad = (boxList, isVerifyRequired, columnList) => {
      setState({
        boxes: boxList,
        fields: fieldKeyList,
        columns: columnList,
        isVerifyRequired,
        verificationUser,
        dataEntryUser,
        templateFields: fieldKeyList,
        assignedUsers,
        autoTransmit: jobDoc.autoTransmit,
        jobStatus: jobDoc.jobStatus,
        jobId: jobDoc.jobId,
        country: jobDoc.countryCode,
        skpinstance: jobDoc.skpinstance,
        regexFormat,
        jobDetails: jobDoc,
        showTable: true,
      })
      setTableLoading(false)
      refreshValidationErrors(boxList)
    }

    if (sortedDocs.length > QC_BOX_ROWS_BUILD_BATCH) {
      return new Promise((resolve) => {
        boxRowsBuildCancelRef.current = buildBoxRowsBatched(sortedDocs, editableFieldNames, (batchedBoxList) => {
          const { columnList: columnsWithData, isVerifyRequired: verifyRequired } = buildBoxColumnsFromFields(
            fieldKeyList,
            batchedBoxList,
            JobId,
            jobDoc,
            regexFormat,
            t,
          )
          finishLoad(batchedBoxList, verifyRequired, columnsWithData)
          resolve(batchedBoxList)
        })
      })
    }

    const boxList = sortedDocs.map((doc) => buildSingleBoxRow(doc, editableFieldNames))
    const { columnList: columnsWithData, isVerifyRequired: verifyRequired } = buildBoxColumnsFromFields(
      fieldKeyList,
      boxList,
      JobId,
      jobDoc,
      regexFormat,
      t,
    )
    finishLoad(boxList, verifyRequired, columnsWithData)
    return boxList
  }

  return (
    <MainRouteSubRoutePage
      mainTitle='routeTitles.dataEntry'
      subTitle='routeTitles.boxQualityCheck'
      buttons={
        <Buttons
          changeApprove={changeApprove}
          disabled={tableLoading || isGridRowEditing}
          onClickApprove={openApproveModal}
          onClickReject={openRejectModal}
          onClickCancel={openCancelModal}
          manualTransmitJob={manualTransmitJob}
          t={t}
        />
      }
    >
      {/* AI code — compact spacing for QC pages */}
      <SummaryHeader jobId= {JobId} boxId= {BoxId} dataKeyed= {state.boxes.length} templateType = {false} compact />
      {state.showTable && (
        <QcDataGridBulkUpdateTable
          fileList={state.boxes}
          columnsList={state.columns}
          tableRef={tableRef}
          jobId={JobId}
          boxId={BoxId}
          dataEntryType={getState()?.jobDetails?.dataEntryType}
          jobStatus={state.jobStatus}
          qualityCheck={true}
          editTable={true}
          isFromQC={true}
          localEditOnly={true}
          filesLoading={tableLoading}
          onSelectionChange={onSelectionChange}
          onValidationChange={handleGridValidationChange}
          cellValidationErrors={validationErrors} // AI code
          onRowsUpdated={handleRowsUpdated} // AI code
        />
      )}
    </MainRouteSubRoutePage>
  )
}

export default BoxFlowForQualityCheck